interface TranslationSet {
  [key: string]: string
}

interface Translations {
  [lang: string]: {
    general: TranslationSet
    tooltipsExpenses: {
      [assetType: string]: TranslationSet
    }
    tooltipsImprovements: {
      [assetType: string]: TranslationSet
    }
  }
}

export const translations: Translations = {
  en: {
    general: {
      // Asset Types
      inventory: "Inventory: Crop Science",
      medical_device: "Medical Device",
      high_value: "High Value",
      chemical: "Chemical",
      pharma: "Pharma",
      cold_chain: "Cold Chain",
      asset_tracking: "Asset Tracking",

      // Expense Labels
      // inventory: 'Inventory: Crop Science'
      imprecise_production: "Imprecise Production Planning - Excessive Labor",
      lost_revenue:
        "Sales - Lost Revenue, Opportunity Cost, Wrong Product Inventory Mix",

      // medical_device: 'Medical Device',
      revenue_realization:
        "Improve Revenue Realization Due to Consumption Notifications",
      shipment_disputes: "Shipment Disputes",

      // medical_device: 'Medical Device',
      // inventory: 'Inventory: Crop Science'
      excessive_scrap: "Excessive Scrap - Product Expiry", // shared
      excessive_inventory: "Excessive Inventory - Carrying Expense", // shared
      premium_shipping: "Excessive Shipment Expenses - Premium Shipping", // shared

      // high_value: 'High Value',
      excessive_theft: "Excessive Theft",
      counterfeit_delivery: "Counterfeit Added During Delivery",
      damage_drop: "Damage - Drop or Vibration",
      insurance_subrogation: "Insurance Subrogation",
      value_added_service: "Value-Added Service",

      // chemical: 'Chemical',
      spoilage_temperature: "Spoilage - Temperature Alert to Correct Condition",

      // pharma: 'Pharma',
      temp_excursions: "Temp. excursions - reduce or damage efficacy",
      regulatory_compliance_automated_records:
        "Regulatory Tracking Compliance and Automated Records",

      // cold_chain: 'Cold Chain',
      product_spoilage:
        "Product Spoilage - Temperature Alert to Correct Condition",
      shipping_insights: "Shipping Insights to Improve Processes",

      // chemical: 'Chemical',
      // pharma: 'Pharma',
      // cold_chain: 'Cold Chain',
      just_in_time:
        "Just in Time Delivery - 2nd Shipment in Event Initial Shipment Out of Spec.",
      customer_penalty: "Avoid Customer Penalty Due to Out of Spec. Product",
      improve_customer_satisfaction: "Improve customer satisfaction",

      // asset_tracking: 'Asset Tracking - Rotable Parts',
      inventory_availability: "Inventory Availability",
      lost_assets: "Lost / Delayed Assets",
      freight: "Freight (Unnecessary/Premium)",
      maintenance_sla: "Maintenance Vendor SLA",
      regulatory_compliance: "Regulatory Compliance",

      // General UI Labels
      select_asset_type: "► Choose your asset type:",
      start: "► Start",
      next: "► Next",
      back: "Back ◄",
      calculate: "► Calculate",
      restart: "Restart ◄",
      select_asset_type_first: "Please select an Asset Type first.",
      talk_about_shipments: "Now let's talk about your shipments",

      // Shipments
      total_yearly_volume: "Total Yearly Asset Shipments (volume)",
      percentage_to_track: "Percentage to Track",
      number_of_tracked_assets: "Number Of Tracked Assets",

      // Header
      roi_calculator: "ROI CALCULATOR",
      stop_guessing: "Stop Guessing, Start Saving",
      supercharge_supply_chain:
        "See How Sensors Can Supercharge Your Supply Chain ROI",
      asset_type_subtitle: `Tired of gut feelings and Excel spreadsheets? This free ROI calculator reveals the hidden cash hiding in your blind spots.`,
      shipments_subtitle: `Now let's talk about your shipments`,
      business_expense_subtitle: "And now, your business expense",
      business_improvement_subtitle:
        "What percentage of business improvement do you forecast using Sensos Labels?",

      // Footer
      privacy_policy: "Privacy Policy",
      terms_conditions: "Terms & Conditions",
      all_rights_reserved: "All rights reserved",

      // Result Display
      results: "Results",
      shipments: "Shipments",
      business_expense: "Business added loss/calculated loss",
      business_improvement: "Improvement with Sensos",
      smart_label_investment: "Sensos Solution",
      benefits: "Benefits",
      total_business_loss: "Total Business Loss / Excess Expense",
      total_savings: "TOTAL SAVINGS",
      label_price: "Label Price",
      cloud_subscription: "Cloud Subscription",
      annual_investment: "Annual Investment",
      label_lifecycle: "Label Lifecycle (years)",
      business_improvement_net: "Business Improvement (Net)",
      payback_term_months: "Payback Term (Months)",
      roi: "ROI %",

      // Validation errors
      asset_type_error: "Please select an asset type first.",
      yearly_volume_error:
        "This field is required and must be a positive number.",
      percentage_to_track_error:
        "This field is required and must be between 0 and 100.",
      required_field_error: "This field is required.",
      value_range_error: "Value must be between 0 and 100.",
      positive_number_required: "Please enter a positive number.",

      // AssetTypeInput
      cta_description1: `Ready to unlock the power of sensors? Stop playing the guessing game.`,
      cta_description2: `Enter your details and see how much you could be saving today!`,
      slash_costs_title: "Slash costs",
      slash_costs_description: `Reduce waste, optimize deliveries, and say goodbye to hidden inefficiencies.`,
      boost_efficiency_title: "Boost efficiency",
      boost_efficiency_description: `Get real-time data to make smarter decisions, faster. Streamline operations and leave guesswork behind.`,
      supercharge_satisfaction_title: "Supercharge customer satisfaction",
      supercharge_satisfaction_description: `Deliver on-time, every time. See happy customers and watch your bottom line smile.`,

      asset_type: "Asset Type",
      label_lifecycle_years: "Label Lifecycle (years)",

      select_placeholder: "Choose your asset type ▼",
      unknown: "",
      value_between_0_and_100: "Value should be between 0 and 100",

      tooltipText: "tooltipText",
    },
    tooltipsExpenses: {
      inventory: {
        total_yearly_volume:
          "Enter the total number of units you ship annually. This helps calculate the scale of your logistics operations.",
        percentage_to_track:
          "Estimate the percentage of your shipments that you'd benefit most from tracking (e.g., high-value, time-sensitive). Focus on the portion of your shipments where improved visibility makes the biggest difference.",
        number_of_tracked_assets:
          "This will be automatically calculated based on the previous two fields. This is the estimated number of shipments you'll be tracking with Sensos.",
        imprecise_production:
          "Estimate the annual cost of labor wasted due to production inefficiencies caused by inaccurate inventory data. Consider overtime, idle time, and expedited production runs.",
        excessive_inventory:
          "Estimate the annual cost of storing excess inventory, including warehousing, insurance, spoilage, and obsolescence. Think about the costs tied up in products sitting in your warehouse.",
        lost_revenue:
          "Estimate the annual revenue lost due to stockouts, missed sales opportunities, or having the wrong products in stock. Include the potential value of sales you couldn′t make.",
        excessive_scrap:
          "Estimate the annual cost of products that expire or become unusable due to improper storage or handling. Specifically for Crop Science, this could be seed viability or chemical effectiveness.",
        premium_shipping:
          "Estimate the annual cost of expedited or premium shipping due to delays, last-minute orders, or inefficient logistics. How much extra do you spend to get things where they need to be, when they need to be there, because of avoidable issues?",
      },

      medical_device: {
        total_yearly_volume:
          "Enter the total number of medical devices or units you ship annually. This helps us understand the scale of your distribution network.",
        percentage_to_track:
          "Estimate the percentage of your shipments that you'd benefit most from real-time tracking (e.g., high-value, time-sensitive, regulated). Focus on the shipments where visibility is most critical.",
        number_of_tracked_assets:
          "This will be automatically calculated based on the previous two fields. This is the estimated number of shipments you'll be tracking with Sensos.",
        excessive_scrap:
          "Estimate the annual cost of medical devices that expire or become unusable due to improper storage, handling, or lost visibility. Consider the value of wasted inventory due to expiry or obsolescence.",
        excessive_inventory:
          "Estimate the annual cost of storing excess medical device inventory, including warehousing, insurance, and potential obsolescence. Think about the costs associated with holding too much inventory.",
        premium_shipping:
          "Estimate the annual cost of expedited or premium shipping due to delays, last-minute orders, or inefficient logistics. How much extra do you spend to meet urgent demands or rectify shipping errors?",
        revenue_realization:
          "Estimate the potential increase in revenue you could realize by receiving timely notifications when devices are used or require replenishment. Consider the value of preventing stockouts and ensuring timely replacements.",
        shipment_disputes:
          "Estimate the annual cost associated with resolving shipment disputes, including investigations, replacements, and potential chargebacks. How much do you lose annually due to discrepancies in deliveries?",
      },

      high_value: {
        total_yearly_volume:
          "Enter the total number of high-value assets you ship annually. This helps us understand the scale of your logistics operations.",
        percentage_to_track:
          "Estimate the percentage of your high-value shipments that you need to track in real-time. Focus on the most critical or vulnerable assets.",
        number_of_tracked_assets:
          "This will be automatically calculated based on the previous two fields. This is the estimated number of shipments you'll be tracking with Sensos.",
        excessive_theft:
          "Estimate the annual cost of stolen high−value assets during transit or storage. Consider the replacement value and any associated losses.",
        counterfeit_delivery:
          "Estimate the annual cost associated with counterfeit goods being introduced into your supply chain during the delivery process. This includes the cost of recalls, reputational damage, and lost revenue.",
        damage_drop:
          "Estimate the annual cost of damage to high−value assets due to drops, vibrations, or other environmental factors during shipping. Consider the repair or replacement costs.",
        insurance_subrogation:
          "Estimate the annual cost of insurance claims related to lost, stolen, or damaged high-value assets. Include deductibles, increased premiums, and administrative costs.",
        value_added_service:
          "Estimate the annual cost of value-added services (e.g., specialized handling, white-glove delivery) that you could potentially reduce with improved logistics and tracking. How much could you save by streamlining your high-value asset handling?",
      },

      chemical: {
        total_yearly_volume:
          "Enter the total number of chemical shipments (e.g., containers, pallets) you send annually. This helps us understand the scale of your distribution network.",
        percentage_to_track:
          "Estimate the percentage of your chemical shipments that require real-time tracking due to safety concerns, regulatory requirements, or high value. Focus on the shipments where visibility is most critical.",
        number_of_tracked_assets:
          "This will be automatically calculated based on the previous two fields. This is the estimated number of shipments you'll be tracking with Sensos.",
        spoilage_temperature:
          "Estimate the annual cost of chemical spoilage or degradation due to temperature fluctuations during transit or storage. Consider the value of lost product that could be salvaged with timely temperature alerts.",
        just_in_time:
          "Estimate the annual cost of expedited or second shipments required to meet delivery deadlines when the initial shipment is compromised due to temperature issues. How much extra do you spend to replace spoiled or unusable chemicals?",
        customer_penalty:
          "Estimate the annual cost of penalties, chargebacks, or lost business due to delivering chemical products that are outside of the required specifications. How much do you lose when you deliver a product that doesn′t meet quality standards?",
        improve_customer_satisfaction:
          "Estimate the annual financial impact of improved customer satisfaction due to on-time deliveries and high-quality products. Consider the increased customer loyalty, repeat business, and positive word-of-mouth referrals.",
      },

      pharma: {
        total_yearly_volume:
          "Enter the total number of pharmaceutical shipments (e.g., vials, containers, pallets) you send annually. This helps us understand the scale of your distribution network.",
        percentage_to_track:
          "Estimate the percentage of your pharmaceutical shipments that require real-time tracking due to temperature sensitivity, regulatory requirements, or high value. Focus on the most critical or vulnerable shipments.",
        number_of_tracked_assets:
          "This will be automatically calculated based on the previous two fields. This is the estimated number of shipments you'll be tracking with Sensos.",
        temp_excursions:
          "Estimate the annual cost of pharmaceutical product spoilage or reduced efficacy due to temperature fluctuations during transit or storage. Consider the value of lost products that could be salvaged with time-temperature alerts.",
        just_in_time:
          "Estimate the annual cost of expedited or second shipments required to meet delivery deadlines when the initial shipment is compromised due to temperature issues or other quality deviations. How much extra do you spend to replace compromised pharmaceuticals?",
        customer_penalty:
          "Estimate the annual cost of penalties, chargebacks, or lost business and deliver pharmaceutical products that are outside of the required specifications. How much do you lose when you deliver a product that doesn’t meet quality standards?",
        improve_customer_satisfaction:
          "Estimate the annual financial impact of improved customer satisfaction due to on-time deliveries and high-quality products. Consider the increased customer loyalty, repeat business, and positive word-of-mouth referrals.",
        regulatory_compliance_automated_records:
          "Estimate the annual cost of manual processes, potential fines, or inefficiencies related to regulatory tracking and record-keeping for pharmaceutical shipments. How much could you save by automating compliance and reporting?",
      },

      cold_chain: {
        total_yearly_volume:
          "Enter the total number of temperature-sensitive shipments (e.g., pallets, containers) you send annually. This helps us understand the scale of your cold chain operations.",
        percentage_to_track:
          "Estimate the percentage of your cold chain shipments that require real-time temperature monitoring and tracking. Focus on the most critical or vulnerable products.",
        number_of_tracked_assets:
          "This will be automatically calculated based on the previous two fields. This is the estimated number of shipments you'll be tracking with Sensos.",
        product_spoilage:
          "Estimate the annual cost of product spoilage or degradation due to temperature excursions during transit or storage. Consider the value of lost product that could be salvaged with timely temperature alerts.",
        just_in_time:
          "Estimate the annual cost of expedited or second shipments required to meet delivery deadlines when the initial shipment is compromised due to temperature issues. How much extra do you spend to replace spoiled or unusable goods?",
        customer_penalty:
          "Estimate the annual cost of penalties, chargebacks, or lost business due to delivering products that are outside of the required temperature range. How much do you lose when you deliver a product that doesn’t meet quality standards?",
        improve_customer_satisfaction:
          "Estimate the annual financial impact of improved customer satisfaction due to on-time deliveries and high-quality products. Consider the increased customer loyalty, repeat business, and positive word-of-mouth referrals.",
        shipping_insights:
          "Estimate the annual cost of inefficiencies in your cold chain shipping processes that could be improved with better data insights. Think about areas where better visibility could lead to cost savings or process optimization.",
      },

      asset_tracking: {
        total_yearly_volume:
          "Enter the total number of assets you ship or manage annually. This helps us understand the scale of your asset tracking needs.",
        percentage_to_track:
          "Estimate the percentage of your assets that require active tracking due to value, criticality, or operational requirements. Focus on the assets where real-time visibility is most crucial.",
        number_of_tracked_assets:
          "This will be automatically calculated based on the previous two fields. This is the estimated number of assets you'll be tracking with Sensos.",
        inventory_availability:
          "Estimate the annual cost of lost revenue or operational disruptions due to assets being unavailable when needed. Consider downtime, project delays, or missed opportunities to assess availability.",
        lost_assets:
          "Estimate the annual cost of lost, stolen, or delayed assets, including replacement costs, investigation expenses, and operational disruptions. How much do you lose due to assets going missing or arriving late?",
        freight:
          "Estimate the annual cost of unnecessary or premium freight charges due to inefficient asset tracking or logistics. How much could you save by optimizing asset utilization and logistics?",
        maintenance_sla:
          "Estimate the annual cost of maintenance vendor service level agreements (SLAs) that are impacted by inefficient asset tracking or management. How much could you save by streamlining maintenance processes with better asset visibility?",
        regulatory_compliance:
          "Estimate the annual cost of ensuring regulatory compliance related to asset tracking and reporting, including potential fines or penalties. How much could you save by automating compliance and reporting processes?",
      },
    },
    tooltipsImprovements: {
      inventory: {
        imprecise_production:
          "Estimate the percentage reduction in labor costs you expect to achieve with improved inventory visibility and planning. How much more efficient could your production be with accurate, real-time inventory?",
        excessive_inventory:
          "Estimate the percentage reduction in carrying costs you expect to achieve by optimizing inventory levels. How much could you save by having the right amount of stock on hand?",
        lost_revenue:
          "Estimate the percentage increase in sales you expect to achieve by having the right products in the right place at the right time. How much more revenue could you generate by minimizing stockouts and maximizing sales opportunities?",
        excessive_scrap:
          "Estimate the percentage reduction in scrap costs you expect to achieve with better inventory management and tracking. How much could you save by preventing product expiry and waste?",
        premium_shipping:
          "Estimate the percentage reduction in premium shipping costs you expect to achieve with improved logistics and planning. How much could you save by avoiding rush orders and expedited shipping?",
      },

      medical_device: {
        excessive_scrap:
          "Estimate the percentage reduction in scrap costs you expect to achieve with improved inventory tracking and management. How much could you save by minimizing expired or unusable devices?",
        excessive_inventory:
          "Estimate the percentage reduction in carrying costs you expect to achieve by optimizing inventory levels and reducing storage needs. How much could you save by having the right amount of stock on hand?",
        premium_shipping:
          "Estimate the percentage reduction in premium shipping costs you expect to achieve with improved logistics and planning. How much could you save by avoiding rush orders and expedited shipping?",
        revenue_realization:
          "Estimate the percentage increase in revenue you expect to achieve by receiving timely notifications about device usage and replenishment needs. How much more revenue could you generate by ensuring product availability and timely replacements?",
        shipment_disputes:
          "Estimate the percentage reduction in costs associated with shipment disputes you expect to achieve with improved tracking and proof of delivery. How much could you save by minimizing discrepancies and providing clear evidence of deliveries?",
      },

      high_value: {
        excessive_theft:
          "Estimate the percentage reduction in theft incidents you expect to achieve with improved tracking and security measures. How much more secure could your high-value shipments be?",
        counterfeit_delivery:
          "Estimate the percentage reduction in counterfeit incidents you expect to achieve with enhanced visibility and tracking. How much could you protect your brand and customers by minimizing counterfeit risks?",
        damage_drop:
          "Estimate the percentage reduction in damage costs you expect to achieve with real-time monitoring of environmental conditions and handling. How much could you save by preventing damage during transit?",
        insurance_subrogation:
          "Estimate the percentage reduction in insurance claim costs you expect to achieve with improved tracking and proof of delivery. How much could you save by minimizing losses and streamlining the claims process?",
        value_added_service:
          "Estimate the percentage reduction in value-added service costs you expect to achieve with optimized logistics and handling. How much could you streamline your operations and reduce unnecessary expenses?",
      },

      chemical: {
        spoilage_temperature:
          "Estimate the percentage reduction in spoilage costs you expect to achieve with real-time temperature monitoring and alerts. How much could you save by preventing temperature-related spoilage?",
        just_in_time:
          "Estimate the percentage reduction in expedited shipping costs you expect to achieve by preventing temperature-related issues and ensuring on-time delivery of the initial shipment. How much could you save by avoiding last-minute replacements?",
        customer_penalty:
          "Estimate the percentage reduction in penalties or lost revenue due to delivering out-of-spec chemical products. How much could you save by ensuring product quality and compliance?",
        improve_customer_satisfaction:
          "Estimate the percentage increase in customer satisfaction (and its associated financial benefits) due to improved delivery performance and product quality. How much could you gain from happier, more loyal customers?",
      },

      pharma: {
        temp_excursions:
          "Estimate the percentage reduction in spoilage costs you expect to achieve with real-time temperature monitoring and alerts. How much could you save by preventing temperature-related spoilage?",
        just_in_time:
          "Estimate the percentage reduction in expedited shipping costs you expect to achieve by preventing temperature-related issues and ensuring on-time delivery of the initial shipment. How much could you save by avoiding last-minute replacements?",
        customer_penalty:
          "Estimate the percentage reduction in penalties or lost revenue due to delivering out-of-spec pharmaceutical products. How much could you save by ensuring product quality and compliance?",
        improve_customer_satisfaction:
          "Estimate the percentage increase in customer satisfaction (and its associated financial benefits) due to improved delivery performance and product quality. How much could you gain from happier, more loyal customers?",
        regulatory_compliance_automated_records:
          "Estimate the percentage reduction in costs or time spent on regulatory tracking and record-keeping due to automation. How much could you streamline compliance and reporting?",
      },

      cold_chain: {
        product_spoilage:
          "Estimate the percentage reduction in spoilage costs you expect to achieve with real-time temperature monitoring and alerts. How much could you save by preventing temperature-related spoilage?",
        just_in_time:
          "Estimate the percentage reduction in expedited shipping costs you expect to achieve by preventing temperature-related issues and ensuring on-time delivery of the initial shipment. How much could you save by avoiding last-minute replacements?",
        customer_penalty:
          "Estimate the percentage reduction in penalties or lost revenue due to delivering products outside of the required temperature range. How much could you save by ensuring product quality and compliance?",
        improve_customer_satisfaction:
          "Estimate the percentage increase in customer satisfaction (and its associated financial benefits) due to improved delivery performance and product quality. How much could you gain from happier, more loyal customers?",
        shipping_insights:
          "Estimate the percentage improvement in shipping process efficiency and cost reduction you expect to achieve with better data insights. How much could you streamline your cold chain logistics with better visibility and analysis?",
      },

      asset_tracking: {
        inventory_availability:
          "Estimate the percentage improvement in asset availability you expect to achieve with real-time tracking and optimized asset management. How much more readily available could your assets be when needed?",
        lost_assets:
          "Estimate the percentage reduction in lost, stolen, or delayed assets you expect to achieve with improved tracking and security measures. How much could you save by minimizing asset loss and delays?",
        freight:
          "Estimate the percentage reduction in unnecessary or premium freight charges you expect to achieve with optimized asset logistics. How much could you save by streamlining asset movement and reducing expedited shipping?",
        maintenance_sla:
          "Estimate the percentage improvement in maintenance vendor SLA compliance you expect to achieve with better asset tracking and management. How much more efficient could your maintenance processes be with real-time asset insights?",
        regulatory_compliance:
          "Estimate the percentage reduction in costs or time spent on regulatory compliance related to asset tracking and reporting due to automation. How much could you streamline compliance and reporting with automated tools?",
      },
    },
  },
}

export const translate = (
  key: string,
  lang: string = "en",
  assetType?: string,
  type: "expenses" | "improvements" = "expenses",
) => {
  const translationCategory =
    type === "improvements"
      ? "tooltipsImprovements"
      : ("tooltipsExpenses" as const)

  // Check if it's an asset-type-specific tooltip for the given type
  if (
    assetType &&
    translations[lang]?.[translationCategory]?.[assetType]?.[key]
  ) {
    return translations[lang][translationCategory][assetType][key]
  }

  // Check general UI labels
  return translations[lang]?.general[key] || key
}
